import { Container, Row } from '@farol-ds/react'
import { assignSubComponents } from '@farol-ds/react/utilities/internal'
import { useComposedRefs } from '@radix-ui/react-compose-refs'
import classNames from 'classnames'
import { forwardRef, HTMLAttributes, ReactNode, useContext, useEffect, useRef } from 'react'

import styles from './appbar.module.scss'
import AppBarEnd from './appbar-end'
import AppBarProvider from './appbar-provider'
import AppBarStart from './appbar-start'
import AppBarStartContent from './appbar-start-content'
import AppBarStickyTrigger from './appbar-sticky-trigger'
import { AppBarContext } from './context'

export type AppBarElement = HTMLDivElement

export interface AppBarProps extends HTMLAttributes<AppBarElement> {
  children: ReactNode
  className?: string
}

const AppBarRoot = forwardRef<AppBarElement, AppBarProps>(function AppBar(props, forwardedRef) {
  const { className, children, ...rest } = props

  const { mode, stickyTriggerRef } = useContext(AppBarContext)
  const rootRef = useRef<HTMLDivElement>(null)
  const refs = useComposedRefs(forwardedRef, rootRef)

  const classes = classNames(
    styles.vars,
    styles.root,
    {
      [styles.sticky]: mode === 'sticky',
    },
    className
  )

  // sticky mode
  useEffect(() => {
    if (mode !== 'sticky') return
    if (!stickyTriggerRef.current) return warnDevAboutNoStickyTrigger()
    const observer = new IntersectionObserver(
      ([entry]) => {
        const shouldStick = entry.boundingClientRect.top < 0 && !entry.isIntersecting
        rootRef.current?.classList.toggle(styles.stickyVisible, shouldStick)
        document.documentElement.classList.toggle(styles.vars, shouldStick)
      },
      { threshold: 0 }
    )
    observer.observe(stickyTriggerRef.current)
    return () => observer.disconnect()
  }, [mode, stickyTriggerRef])

  return (
    <div {...rest} className={classes} ref={refs}>
      <Container>
        <Row>{children}</Row>
      </Container>
    </div>
  )
})

function warnDevAboutNoStickyTrigger() {
  if (process.env.NODE_ENV === 'development') {
    throw new Error(
      'Make sure to instantiate the <AppBar.StickyTrigger /> component inside the <AppBar.Provider /> component'
    )
  }
}

export const AppBar = assignSubComponents('AppBar', AppBarRoot, {
  Provider: AppBarProvider,
  StickyTrigger: AppBarStickyTrigger,
  // UI
  Start: AppBarStart,
  StartContent: AppBarStartContent,
  End: AppBarEnd,
})

export * from './appbar-end'
export * from './appbar-provider'
export * from './appbar-start'
export * from './appbar-start-content'
export * from './appbar-sticky-trigger'
